/**
 * Button Component
 *
 * A versatile button component with multiple variants and sizes.
 * Follows the clean, minimalistic design inspired by modern dashboards.
 */

import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/cn';
import type { ButtonProps } from './button.types';

const buttonVariants = cva(
  // Base styles - clean, modern hierarchy pattern
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-[var(--a-primary)] disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
  {
    variants: {
      variant: {
        // Primary: Solid brand color - for main CTAs
        primary:
          'bg-[var(--a-primary-action)] text-[var(--a-primary-action-fg)] hover:bg-[var(--a-primary-action-hover)] active:bg-[var(--a-primary-action-hover)] shadow-sm hover:shadow',
        // Secondary: White with border - for important secondary actions
        secondary:
          'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100 shadow-sm',
        // Tertiary/Ghost: Subtle - for less important actions
        tertiary:
          'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300',
        // Ghost: Minimal - for cancel, dismiss actions
        ghost:
          'bg-transparent text-gray-600 hover:bg-gray-100 active:bg-gray-200',
        // Quiet: One step quieter than ghost — borderless, no hover
        // background, just a color shift on hover. Used by the design's
        // footer-style affordances ("Back", "Use defaults", "Skip for
        // now"). Distinct from ghost so designs that want both levels
        // (a cancel chip AND a skip link) can express both.
        quiet:
          'bg-transparent text-gray-500 hover:text-gray-900 active:text-gray-900',
        // Danger: For destructive actions
        danger:
          'bg-error-500 text-white hover:bg-error-600 active:bg-error-700 shadow-sm hover:shadow focus-visible:outline-[var(--a-danger)]',
        // Choice is the shared card-like action tier for selecting one
        // option from a short set, such as POS payment methods.
        choice:
          'rounded-xl border border-[var(--a-line)] bg-[var(--a-surface)] text-left text-[14px] font-semibold text-[var(--a-text)] hover:bg-[var(--a-surface-2)] active:bg-[var(--a-surface-2)]',
      },
      size: {
        sm: 'h-9 px-3 py-1.5 text-sm',
        md: 'h-10 px-4 py-2.5 text-base',
        lg: 'h-12 px-6 py-3 text-lg',
        // Phase 4.4c-1 — canonical Invento form-control size used by
        // auth + onboarding screens. 42 high, 10 radius, 14/500.
        // Existing sm/md/lg unchanged so no current consumer is forced
        // off them.
        app: 'h-[42px] px-4 py-0 text-sm rounded-[10px]',
        dense:
          'h-9 px-3.5 py-0 text-[13px] rounded-lg border border-solid [font-family:var(--a-body)] !duration-[140ms] !shadow-none hover:!shadow-none disabled:!opacity-70',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      iconOnly: {
        true: 'h-10 w-10 px-0',
        false: '',
      },
      iconOnlySize: {
        default: '',
        compact: 'h-9 w-8 px-0 py-0 text-[15px]',
      },
      choiceLayout: {
        tile: 'grid grid-cols-1 h-auto min-h-[238px] justify-start gap-2 rounded-[14px] !p-4 text-left [font-family:var(--a-body)]',
        option: 'grid h-auto min-h-[118px] justify-start gap-2 rounded-[14px] p-3.5 text-left [font-family:var(--a-body)]',
        compact: 'min-h-[72px] justify-start gap-3 rounded-[14px] border-dashed px-4 py-3 text-left [font-family:var(--a-body)]',
      },
    },
    // Keep app/dense primary variants explicit so composed consumers retain
    // the shared action tier even when they add their own utility classes.
    compoundVariants: [
      {
        size: 'app',
        variant: 'primary',
        class:
          '!bg-[var(--a-primary-action)] !text-[var(--a-primary-action-fg)] hover:!bg-[var(--a-primary-action-hover)] active:!bg-[var(--a-primary-action-hover)]',
      },
      {
        size: 'app',
        variant: 'secondary',
        class:
          '!bg-[var(--a-surface)] !text-[var(--a-text)] !border-[var(--a-line)] hover:!bg-[var(--a-surface-2)]',
      },
      {
        size: 'app',
        variant: 'ghost',
        class:
          '!bg-transparent !text-[var(--a-text-soft)] hover:!bg-[var(--a-surface-2)] hover:!text-[var(--a-text)]',
      },
      {
        size: 'app',
        variant: 'quiet',
        class:
          '!bg-transparent !text-[var(--a-text-soft)] hover:!text-[var(--a-text)]',
      },
      {
        size: 'app',
        variant: 'danger',
        class:
          '!bg-[var(--a-danger)] !text-white hover:!bg-[var(--a-danger)] active:!bg-[var(--a-danger)] focus-visible:!outline-[var(--a-danger)]',
      },
      {
        size: 'dense',
        variant: 'primary',
        class:
          '!border-transparent !bg-[var(--a-primary-action)] !text-[var(--a-primary-action-fg)] hover:!bg-[var(--a-primary-action-hover)] active:!bg-[var(--a-primary-action-hover)]',
      },
      {
        size: 'dense',
        variant: 'ghost',
        class:
          '!border-[var(--a-line)] !bg-transparent !text-[var(--a-text)] hover:!bg-transparent active:!bg-transparent',
      },
      {
        size: 'dense',
        variant: 'quiet',
        class:
          '!border-transparent !bg-transparent !text-[var(--a-text-soft)] hover:!bg-transparent hover:!text-[var(--a-text-soft)] active:!text-[var(--a-text-soft)]',
      },
      {
        size: 'dense',
        variant: 'danger',
        class:
          '!border-[var(--a-line)] !bg-transparent !text-[#B7305A] hover:!bg-transparent active:!bg-transparent focus-visible:!outline-[#B7305A]',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
      fullWidth: false,
      iconOnly: false,
    },
  }
);

const DenseLoadingSpinner = () => (
  <span
    aria-hidden
    className="inline-block h-3 w-3 shrink-0 animate-[spin_0.7s_linear_infinite] rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
  />
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading,
      leftIcon,
      rightIcon,
      iconOnly,
      iconOnlySize,
      choiceLayout,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDense = size === 'dense';

    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth, iconOnly, iconOnlySize, choiceLayout, className }))}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            {isDense ? <DenseLoadingSpinner /> : <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />}
            {children}
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
